import {
  fieldPropsEmail,
  fieldPropsNationalIdentificationNumber,
  fieldPropsPhoneNumber
} from '~/App/shared/components/Fields/shared/fieldProps';
import { FormData, FormField } from './DynamicForm';
import { scroller } from 'react-scroll';

export const getFiledPropsFetcher = (
  textFieldType: FormField['textFieldType']
) => {
  switch (textFieldType) {
    case 'email':
      return fieldPropsEmail;
    case 'nationalIdentificationNumber':
      return fieldPropsNationalIdentificationNumber;
    case 'phoneNumber':
      return fieldPropsPhoneNumber;
    default:
      return null;
  }
};

export const scrollTo = (id: string, headerHeight: number) =>
  scroller.scrollTo(id, {
    duration: 500,
    delay: 100,
    smooth: true,
    isDynamic: true,
    offset: (headerHeight + 32) * -1
  });

export const getOtherKey = (backendKey: string) => `${backendKey}_other`;

export const getOtherLabel = ({
  fieldType,
  allowOtherOption,
  otherOptionLabel
}: FormField) =>
  fieldType === 'generic-multipleChoice' && allowOtherOption && otherOptionLabel
    ? otherOptionLabel
    : null;

export const isOtherSelected = (field: FormField, values: FormData) => {
  const otherLabel = getOtherLabel(field);
  const selected = values[field.backendKey];
  return Boolean(
    otherLabel && Array.isArray(selected) && selected.includes(otherLabel)
  );
};

type FollowUpTrigger = { parent: FormField; answer: string };

const getFollowUpTriggers = (fields: FormField[]) =>
  fields.reduce((triggers, parent) => {
    parent.choices?.forEach(({ label, followUpQuestionBackendKey }) => {
      if (!label || !followUpQuestionBackendKey) return;
      triggers.set(followUpQuestionBackendKey, [
        ...(triggers.get(followUpQuestionBackendKey) ?? []),
        { parent, answer: label }
      ]);
    });
    return triggers;
  }, new Map<string, FollowUpTrigger[]>());

export const getFollowUpKeys = (fields: FormField[]) =>
  new Set(getFollowUpTriggers(fields).keys());

export const getVisibleFields = (fields: FormField[], values: FormData) => {
  const triggersByKey = getFollowUpTriggers(fields);
  const visibility = new Map<string, boolean>();

  const isVisible = (field: FormField, visited: Set<string>): boolean => {
    const cached = visibility.get(field.backendKey);
    if (cached !== undefined) return cached;

    const triggers = triggersByKey.get(field.backendKey);
    if (!triggers) return true;

    const nextVisited = new Set([...visited, field.backendKey]);
    const result = triggers.some(({ parent, answer }) => {
      if (nextVisited.has(parent.backendKey)) return true;
      const parentAnswers = values[parent.backendKey];
      return (
        Array.isArray(parentAnswers) &&
        parentAnswers.includes(answer) &&
        isVisible(parent, nextVisited)
      );
    });

    visibility.set(field.backendKey, result);
    return result;
  };

  return fields.filter(field => isVisible(field, new Set()));
};

export const getSubmittedFormData = (fields: FormField[], values: FormData) =>
  getVisibleFields(fields, values).reduce<FormData>((acc, field) => {
    acc[field.backendKey] = values[field.backendKey];
    if (isOtherSelected(field, values)) {
      const otherKey = getOtherKey(field.backendKey);
      acc[otherKey] = values[otherKey];
    }
    return acc;
  }, {});
